import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { STATIC_DATASET_USAGE_KEY } from '@/utils/queryKeys.ts';
import { get_static_dataset_usage_url } from '@/utils/urls.ts';
import { StaticDatasetUsageReport } from '@/types/datasets.ts';

export default function useStaticDatasetUsage(staticDatasetId?: string, history = false) {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  return useQuery({
    queryKey: [...STATIC_DATASET_USAGE_KEY(automationId), staticDatasetId ?? null, history],
    queryFn: async ({ signal }) => {
      const fetchPage = async (page: number): Promise<StaticDatasetUsageReport> => {
        const response = await apiClient.get<StaticDatasetUsageReport>(
          get_static_dataset_usage_url(automationId),
          {
            signal,
            params: {
              static_dataset_id: staticDatasetId,
              history_page: history ? 1 : undefined,
              page_size: 200,
              page,
            },
          },
        );
        if (response.data.page !== page) {
          throw new Error('Invalid data source usage pagination.');
        }
        return response.data;
      };
      let page = 1;
      let report = await fetchPage(page);
      const results = [...report.results];
      while (report.next_page !== null) {
        const nextPage = report.next_page;
        if (!Number.isInteger(nextPage) || nextPage <= page) {
          throw new Error('Invalid data source usage pagination.');
        }
        const next = await fetchPage(nextPage);
        if (report.head_revision !== next.head_revision || report.count !== next.count) {
          throw new Error('Data source usage changed during inspection. Please refresh.');
        }
        results.push(...next.results);
        report = {
          ...report,
          active_complete: report.active_complete && next.active_complete,
          history_complete: report.history_complete && next.history_complete,
          history_errors: report.history_errors || next.history_errors,
          incomplete_reasons: [
            ...new Set([...report.incomplete_reasons, ...next.incomplete_reasons]),
          ],
          results,
          next_page: next.next_page,
        };
        page = nextPage;
      }
      if (
        report.results.length !== report.count ||
        new Set(report.results.map((item) => item.static_dataset_id)).size !== report.count
      ) {
        throw new Error('Data source usage changed during inspection. Please refresh.');
      }
      return report;
    },
    enabled: !!automationId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
